/**
 * SourceMapDecoder - Decodes minified source locations to original files
 * 
 * Loads and caches source maps to decode stack trace locations at log capture time.
 * This ensures original file/line info is stored permanently with each log.
 */

import { SourceMapConsumer } from 'source-map';
import type { SourceLocation } from './WeBelieveLogger.types';

// Cache for loaded source map consumers with metadata
interface CacheEntry {
  consumer: SourceMapConsumer | null;
  loadedAt: number;
  notFound: boolean;  // True if 404 - source map doesn't exist for this bundle
}

const sourceMapCache = new Map<string, CacheEntry>();
const loadingPromises = new Map<string, Promise<SourceMapConsumer | null>>();

// Track the current bundle name for cache management
let currentBundleName: string | null = null;

// Maximum cache entries to prevent memory leaks during long sessions
const MAX_CACHE_ENTRIES = 5;

// Initialize source-map library for browser usage
let initialized = false;
async function initSourceMap(): Promise<void> {
  if (initialized) return;
  
  // source-map library needs WASM initialization in browser
  // The initialize method exists at runtime but not in types
  const consumer = SourceMapConsumer as unknown as { 
    initialize?: (config: Record<string, string>) => void 
  };
  if (typeof consumer.initialize === 'function') {
    consumer.initialize({
      'lib/mappings.wasm': 'https://unpkg.com/source-map@0.7.4/lib/mappings.wasm'
    });
  }
  initialized = true;
}

/**
 * Evict oldest cache entries if we exceed the limit
 */
function evictOldCacheEntries(): void {
  if (sourceMapCache.size <= MAX_CACHE_ENTRIES) return;

  // Sort by loadedAt and remove oldest entries (but keep current bundle)
  const entries = Array.from(sourceMapCache.entries())
    .filter(([name]) => name !== currentBundleName)
    .sort((a, b) => a[1].loadedAt - b[1].loadedAt);

  const toRemove = entries.slice(0, sourceMapCache.size - MAX_CACHE_ENTRIES);
  for (const [name, entry] of toRemove) {
    // Destroy the consumer to free memory
    if (entry.consumer) {
      entry.consumer.destroy();
    }
    sourceMapCache.delete(name);
  }
}

/**
 * Load a source map file from the server
 * Returns null if source map doesn't exist (old bundle)
 */
async function loadSourceMap(bundleFileName: string): Promise<SourceMapConsumer | null> {
  // Check cache first
  const cached = sourceMapCache.get(bundleFileName);
  if (cached) {
    return cached.consumer;
  }

  // Check if already loading
  if (loadingPromises.has(bundleFileName)) {
    return loadingPromises.get(bundleFileName) || null;
  }

  // Start loading
  const loadPromise = (async () => {
    try {
      await initSourceMap();

      const mapUrl = `/assets/${bundleFileName}.map`;
      const response = await fetch(mapUrl);

      if (!response.ok) {
        sourceMapCache.set(bundleFileName, {
          consumer: null,
          loadedAt: Date.now(),
          notFound: true,
        });
        evictOldCacheEntries();
        return null;
      }

      const rawSourceMap = await response.json();
      const consumer = await new SourceMapConsumer(rawSourceMap);
      sourceMapCache.set(bundleFileName, {
        consumer,
        loadedAt: Date.now(),
        notFound: false,
      });
      evictOldCacheEntries();
      return consumer;
    } catch (error) {
      sourceMapCache.set(bundleFileName, {
        consumer: null,
        loadedAt: Date.now(),
        notFound: true,
      });
      return null;
    } finally {
      loadingPromises.delete(bundleFileName);
    }
  })();

  loadingPromises.set(bundleFileName, loadPromise);
  return loadPromise;
}

/**
 * Check if a source map is known to be unavailable (404)
 */
export function isSourceMapUnavailable(bundleFileName: string): boolean {
  const cached = sourceMapCache.get(bundleFileName);
  return cached?.notFound === true;
}

/**
 * Clean up source path from bundler prefixes
 */
function cleanSourcePath(rawSource: string): string {
  let cleanSource = rawSource;

  // Remove bundler prefixes (webpack, vite, rollup, etc.)
  cleanSource = cleanSource.replace(/^(webpack:\/\/|vite:\/\/|rollup:\/\/)/, '');
  // Remove leading ../ sequences
  cleanSource = cleanSource.replace(/^(\.\.\/)+/, '');
  // Remove node_modules prefix if present
  cleanSource = cleanSource.replace(/^node_modules\//, '');
  // Remove leading slashes
  cleanSource = cleanSource.replace(/^\/+/, '');
  // Remove any project name prefix (e.g., "webelieve-frontend/")
  cleanSource = cleanSource.replace(/^[^/]+\/src\//, 'src/');

  // For canvas files, ensure we get the clean path
  if (cleanSource.includes('EdgeDocsCanvas') || cleanSource.includes('EdgeDocsVirtualPages')) {
    // Extract just the relevant path from src/
    const srcMatch = cleanSource.match(/(src\/.*)/);
    if (srcMatch) {
      cleanSource = srcMatch[1];
    }
  }

  return cleanSource;
}

/**
 * Check if a file path looks like a minified bundle that needs decoding
 */
function isMinifiedBundle(file: string): boolean {
  const fileName = file.split('/').pop() || file;
  // Check for Vite bundle patterns: index-xxx.js or assets/index-xxx.js
  return (fileName.includes('index-') && fileName.endsWith('.js')) ||
         file.includes('assets/index-');
}

/**
 * Extract bundle filename from a file path
 */
function extractBundleFileName(file: string): string {
  return file.split('/').pop() || file;
}

/** Patterns for third-party/framework code */
const THIRD_PARTY_PATTERNS = [
  /node_modules/,
  /react-dom/,
  /react\.production/,
  /react\.development/,
  /scheduler/,
  /@babel/,
  /regenerator-runtime/,
];

/** Patterns for logger wrapper files */
const LOGGER_WRAPPER_PATTERNS = [
  /WeBelieveLogger\.(ts|js)/,
  /loggers\/(Wasm|Cache|Canvas|Input|Document|Api|Orchestrator)Logger\.(ts|js)/,
  /utils\/loggers\//,
];

/** Patterns that indicate user source code */
const USER_CODE_PATTERNS = [
  /src\/features\//,
  /src\/components\//,
  /src\/hooks\//,
  /src\/services\//,
  /src\/stores\//,
  /src\/pages\//,
  /src\/app\//,
];

/**
 * Determine if a decoded file path is user code
 */
function isDecodedUserCode(file: string): boolean {
  const isThirdParty = THIRD_PARTY_PATTERNS.some(p => p.test(file));
  const isLoggerWrapper = LOGGER_WRAPPER_PATTERNS.some(p => p.test(file));
  const isUserCode = USER_CODE_PATTERNS.some(p => p.test(file));
  return isUserCode && !isThirdParty && !isLoggerWrapper;
}

/**
 * Decode a minified source location to original file/line
 * Also decodes all frames in the stack array
 *
 * @param source - The source location from stack trace (may be minified)
 * @returns Decoded source location with original file/line, or original if can't decode
 */
export async function decodeSourceLocation(source: SourceLocation): Promise<SourceLocation> {
  if (!source.file && !source.stack?.length) {
    return source;
  }

  // Check if primary file is minified
  const primaryIsMinified = source.file ? isMinifiedBundle(source.file) : false;
  const bundleFileName = source.file ? extractBundleFileName(source.file) : null;

  // Load source map if needed
  let consumer: SourceMapConsumer | null = null;
  if (primaryIsMinified && bundleFileName) {
    try {
      consumer = await loadSourceMap(bundleFileName);
    } catch (error) {
      // Source map loading failed - will use original locations
    }
  }

  // Decode primary location
  let decodedFile = source.file;
  let decodedLine = source.line;
  let decodedColumn = source.column;
  let decodedFunction = source.function;

  if (consumer && primaryIsMinified && source.line) {
    try {
      const original = consumer.originalPositionFor({
        line: source.line,
        column: source.column || 0,
      });

      if (original.source) {
        decodedFile = cleanSourcePath(original.source);
        decodedLine = original.line || undefined;
        decodedColumn = original.column || undefined;
        decodedFunction = original.name || source.function;
      }
    } catch (error) {
      // Keep original values if decode fails
    }
  }

  // Decode stack frames if present
  let decodedStack = source.stack;
  let newCulpritIndex = source.culpritIndex ?? -1;

  if (source.stack && source.stack.length > 0 && consumer) {
    decodedStack = await Promise.all(
      source.stack.map(async (frame) => {
        // Check if this frame needs decoding
        if (!isMinifiedBundle(frame.file)) {
          // Recalculate isUserCode for non-minified frames too
          const newIsUserCode = isDecodedUserCode(frame.file);
          return { ...frame, isUserCode: newIsUserCode };
        }

        try {
          const original = consumer!.originalPositionFor({
            line: frame.line,
            column: frame.column || 0,
          });

          if (original.source) {
            const decodedFilePath = cleanSourcePath(original.source);
            const newIsUserCode = isDecodedUserCode(decodedFilePath);
            return {
              ...frame,
              file: decodedFilePath,
              line: original.line || frame.line,
              column: original.column || frame.column,
              function: original.name || frame.function,
              isUserCode: newIsUserCode,
            };
          }
        } catch (error) {
          // Keep original frame if decode fails
        }

        return frame;
      })
    );

    // Recalculate culprit index based on decoded frames
    newCulpritIndex = decodedStack.findIndex(f => f.isUserCode);
    if (newCulpritIndex === -1) {
      // Fallback: find first non-third-party frame
      newCulpritIndex = decodedStack.findIndex(f =>
        !THIRD_PARTY_PATTERNS.some(p => p.test(f.file))
      );
    }
    if (newCulpritIndex === -1 && decodedStack.length > 0) {
      newCulpritIndex = 0;
    }
  }

  // Update primary location from the new culprit frame if we found one
  if (decodedStack && newCulpritIndex >= 0 && decodedStack[newCulpritIndex]) {
    const culpritFrame = decodedStack[newCulpritIndex];
    decodedFile = culpritFrame.file;
    decodedLine = culpritFrame.line;
    decodedColumn = culpritFrame.column;
    decodedFunction = culpritFrame.function;
  }

  // Return decoded location with full stack and updated culprit
  return {
    ...source, // Keep stackDepth, stackTruncated
    file: decodedFile,
    line: decodedLine,
    column: decodedColumn,
    function: decodedFunction,
    stack: decodedStack,
    culpritIndex: newCulpritIndex >= 0 ? newCulpritIndex : 0,
  };
}

/**
 * Synchronous check if source map is already cached
 * Used to avoid async operations when possible
 */
export function hasSourceMapCached(bundleFileName: string): boolean {
  return sourceMapCache.has(bundleFileName);
}

/**
 * Pre-load source map for current bundle
 * Call this early to have the map ready when logs are created
 */
export async function preloadCurrentSourceMap(): Promise<void> {
  try {
    // Find current bundle by looking at loaded scripts
    const scripts = document.querySelectorAll('script[src*="index-"]');
    for (const script of scripts) {
      const src = (script as HTMLScriptElement).src;
      const match = src.match(/\/assets\/(index-[^.]+\.js)/);
      if (match) {
        // Track current bundle name for cache management
        currentBundleName = match[1];
        await loadSourceMap(match[1]);
        break;
      }
    }
  } catch (error) {
    // Preload failed - will load on demand
  }
}

/**
 * Get the current bundle name (if detected)
 */
export function getCurrentBundleName(): string | null {
  return currentBundleName;
}
